import type { ReactElement, ReactNode } from 'react';
import type { Knock, MeEmberItem, MeEmberState } from '../api';
import { fullDate, provenanceLine, timeAgo } from '../format';
import { withReturnTo } from '../router';
import { Link } from './Link';
import EmberFlame from '../../../../../shared-web/EmberFlame';
import { ActionArrow } from './ActionArrow';
import { KnockDoor } from './KnockLetter';
import './MyEmberCard.css';

/**
 * One row of My Embers (§4.12) at whatever stage it is in: still being
 * written, written but unsaved, published (with private impact totals), or
 * ended without an Ember. No public popularity anywhere, at any stage.
 *
 * A row that IS an Ember reads as an entry in the person's notebook
 * (2026-09-04 second pass): the receipt — how and on which day they told it —
 * where a diary entry carries its date, the title, and the Ember's own first
 * words. The rows that never became an Ember keep their status line, because
 * for them the status is the whole story.
 *
 * An in-flight card with nowhere to go yet renders as a plain div — the
 * hangup-to-webhook window has no conversation to open. Everything else is a
 * link, so a person always has one tap toward their own telling.
 */
export function MyEmberCard(
  { item, letters = [], onOpenLetter, onDelete }: {
    item: MeEmberItem;
    /** The letters about this Ember (§4.9): each a door line with the tab badge's red dot; a tap opens the paper on it. */
    letters?: Knock[];
    onOpenLetter?: (letter: Knock) => void;
    onDelete?: () => void;
  },
): ReactElement {
  const inFlight = item.state === 'writing' || item.state === 'slow';
  const imageUrl = item.media?.header_image_url || '';
  const href = item.href ? withReturnTo(item.href, '/me/embers') : null;
  // A card that can be thrown away carries controls of its own — keep telling
  // it, or delete it — so it cannot be a link wrapping everything (a button
  // inside an anchor is not valid, and not operable). The whole card stops
  // being the target; the actions are. Which states get a Delete is My Embers'
  // call (DELETABLE_STATES), not the card's: the card only asks whether it was
  // handed one. A card with letters is a container too (2026-09-08): each
  // letter line is its own button, the Read link stands in the actions row.
  const hasOwnActions = Boolean(onDelete) || letters.length > 0;
  // The quiet numbers the owner alone sees. The row only stands when one of
  // them has something to say — an Ember nobody has reached yet says nothing.
  const hasMeta = item.read_count > 0 || item.private_resonance_count > 0 || item.gratitude_count > 0;
  const body = (
    <>
      <div className={`my-ember-media${imageUrl ? ' has-image' : ''}`}>
        <div className="my-ember-placeholder" aria-hidden>
          <span />
          <EmberFlame className="my-ember-placeholder-flame" />
        </div>
        {imageUrl && (
          <img
            className="my-ember-image"
            src={imageUrl}
            alt={`${item.title || defaultTitle(item.state)} — illustration`}
            loading="lazy"
            decoding="async"
            onError={(event) => { event.currentTarget.hidden = true; }}
          />
        )}
        {inFlight && <span className="my-ember-writing-sweep" aria-hidden />}
      </div>
      <div className="my-ember-content">
        <div className="my-ember-story-meta">
          <div className="my-ember-story-meta-copy">
            {isEmber(item.state) ? (
              <span className="my-ember-date my-ember-receipt">{receiptLine(item)}</span>
            ) : (
              <>
                <span>{cardLabel(item.state)}</span>
                {dateLine(item) && (
                  <>
                    <span className="my-ember-story-meta-separator" aria-hidden>·</span>
                    <span className="my-ember-date">{dateLine(item)}</span>
                  </>
                )}
              </>
            )}
          </div>
          {/* Unread impact used to sit here as an "N new" pill (until
              2026-09-01); a reader's mattered is a letter line below, with
              the Embers tab's dot (2026-09-25). */}
          {item.state !== 'published' && <StateBadge state={item.state} />}
        </div>
        <div className="my-ember-top">
          <h3 className="my-ember-title" dir="auto">
            {item.title || defaultTitle(item.state)}
          </h3>
        </div>
        {item.excerpt && (
          <p className="my-ember-note my-ember-excerpt" dir="auto">{item.excerpt}</p>
        )}
        {letters.length > 0 && onOpenLetter && (
          <KnockDoor letters={letters} onOpen={onOpenLetter} marked className="is-on-card" />
        )}
        {isEmber(item.state) ? hasMeta && (
          <div className="my-ember-meta">
            {item.private_resonance_count > 0
              ? (
                <span className="my-ember-meta-item">
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="rgba(185, 75, 10, 0.85)" stroke="none" aria-hidden>
                    <path d="M19 14c1.5-1.5 3-3.2 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.8 0-3 .5-4.5 2-1.5-1.5-2.7-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4 3 5.5l7 7Z" />
                  </svg>
                  <span>mattered to {item.private_resonance_count}</span>
                </span>
              )
              : null}
            {item.gratitude_count > 0 && (
              <span className="my-ember-meta-item">
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                <span>{item.gratitude_count} left a note</span>
              </span>
            )}
            {/* How many people have read it (§4.9, 2026-09-24): the first
                thing that happens to an Ember after it is written, and until
                now the one thing the owner could not see. Last in the row
                (2026-09-24 二次): what a reader *said* comes first, the
                number is the quiet one. It stands on a `ready` card too — a
                read is given of those as well. */}
            {item.read_count > 0 && (
              <span className="my-ember-meta-item">
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12Z" />
                  <circle cx="12" cy="12" r="2.6" />
                </svg>
                <span>{item.read_count === 1 ? 'read by 1' : `read by ${item.read_count}`}</span>
              </span>
            )}
          </div>
        ) : !isEmber(item.state) && (
          <>
            <p className="my-ember-note">{note(item.state)}</p>
            {/*
              The story pass names what it is missing and always has; until
              2026-08-19 that list never left the server, so two of these cards
              sitting side by side were indistinguishable and neither said what
              to do. Two lines is the card's share of it — the conversation
              carries the rest.
            */}
            {item.missing_information.length > 0 && (
              <ul className="my-ember-missing">
                {item.missing_information.slice(0, 2).map((line) => (
                  <li key={line} dir="auto">{line}</li>
                ))}
              </ul>
            )}
          </>
        )}
        {hasOwnActions ? (
          <div className="my-ember-actions">
            {href ? (
              <Link
                to={href}
                className="my-ember-open ember-text-action"
                data-log="my_ember_card_open"
                data-log-state={item.state}
                {...(item.conversation_id ? { 'data-log-conversation-id': item.conversation_id } : {})}
              >
                <span>{openLabel(item)}</span>
                <span className="my-ember-arrow">
                  <ActionArrow />
                </span>
              </Link>
            ) : (
              // Nowhere to go yet (a stalled write is with us, not with them)
              // but still deletable. The label stays so the row reads the same
              // as every other card; only Delete is operable.
              <span className="my-ember-open ember-text-action" aria-hidden>{openLabel(item)}</span>
            )}
            {onDelete && (
              <button
                type="button"
                className="my-ember-delete"
                onClick={onDelete}
                data-log="my_ember_card_delete"
                data-log-state={item.state}
              >
                Delete
              </button>
            )}
          </div>
        ) : !inFlight && (
          <div className="my-ember-open ember-text-action" aria-hidden>
            <span>{openLabel(item)}</span>
            {href && (
              <span className="my-ember-arrow">
                <ActionArrow />
              </span>
            )}
          </div>
        )}
      </div>
    </>
  );

  const className = `my-ember-card is-${item.state}${imageUrl ? ' has-image' : ''}`;
  // Exposure rides every card, including the inert ones — a card a person sees
  // and cannot act on is the finding, not a gap. The click name only goes on
  // the linked cards, because an inert card's tap is not a navigation and must
  // not enter the saw→opened funnel as one.
  const logAttrs = {
    'data-log-seen': 'my_ember_card_seen',
    'data-log-state': item.state,
    ...(item.conversation_id ? { 'data-log-conversation-id': item.conversation_id } : {}),
    ...(item.ember_id ? { 'data-log-ember-id': item.ember_id } : {}),
  };
  // Carrying its own actions, or with nowhere to go yet: either way the card
  // itself is a container, not a target. `is-inert` is only the second case —
  // a card with a Delete is very much actionable, just not as one big link.
  if (hasOwnActions || !href) {
    return (
      <div
        className={`${className}${hasOwnActions ? '' : ' is-inert'}`}
        aria-busy={inFlight || undefined}
        {...logAttrs}
      >
        {body}
      </div>
    );
  }
  return <Link to={href} className={className} data-log="my_ember_card_open" {...logAttrs}>{body}</Link>;
}

/** The states that are an Ember — a thing written, whatever its standing. */
function isEmber(state: MeEmberState): boolean {
  return state === 'ready' || state === 'published' || state === 'private';
}

/**
 * The §0.9 receipt on an Ember row: "Told by text · September 4, 2026". An
 * Ember whose source conversation is gone has no channel to name, so its row
 * carries the day alone — the same day the feed has always dated it by.
 */
function receiptLine(item: MeEmberItem): string {
  return provenanceLine(item.provenance) || fullDate(item.activity_at);
}

/** The in-flight and ended states have no title of their own yet. */
function defaultTitle(state: MeEmberState): string {
  switch (state) {
    case 'writing':
      return 'Writing your Ember…';
    // `slow` reads for as long as half an hour and, before the recovery sweep
    // existed, for hours (dev 2026-08-17). A title that keeps saying "Writing…"
    // through all of it is the one thing this card must never be: a live claim
    // about a moment that has passed. Name the wait instead; the note below
    // still carries the promise.
    case 'slow':
      return 'Taking longer than usual';
    case 'stalled':
      return 'This one hit a snag';
    // Not a failure and not a wait: nothing was written because there is not
    // yet a telling here. The card says whose move it is.
    case 'needs_more_context':
      return 'Not a story yet';
    // Says the true thing rather than the tidy one. This telling never reached
    // the writer at all — it ended under the material floor, so there was
    // nothing to write from and no Ember was ever attempted. Calling that a
    // failed review would describe a different event (`failed`, below) and
    // leave the person thinking their story was judged and rejected.
    case 'below_floor':
      return 'Not enough to write yet';
    case 'failed':
      return 'Something went wrong on our side';
    default:
      return 'Untitled Ember';
  }
}

function StateBadge({ state }: { state: MeEmberState }): ReactElement | null {
  const label: Record<MeEmberState, string | null> = {
    writing: 'Waiting',
    slow: 'Waiting',
    stalled: 'Stalled',
    ready: null,
    published: null,
    private: null,
    needs_more_context: 'Keep going',
    below_floor: 'Too short',
    failed: 'Our fault',
  };
  const text = label[state];
  if (!text) return null;
  // A telling being written wears the pen — the one badge that names motion.
  const pen = state === 'writing' || state === 'slow';
  return (
    <span className={`my-ember-badge is-${state}`}>
      {pen && (
        <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
        </svg>
      )}
      <span>{text}</span>
    </span>
  );
}

function cardLabel(state: MeEmberState): string {
  if (state === 'writing' || state === 'slow') return 'Ember';
  if (state === 'stalled' || state === 'failed') return 'Delayed';
  // 'Being written' was a claim, and on a needs_more_context card it was a false
  // one: nothing is being written, and nothing will be until the person says
  // more. Reading "BEING WRITTEN ... Unfinished" a few seconds after hanging up
  // is what made a working judgement look like a broken pipeline (2026-08-19).
  if (state === 'needs_more_context' || state === 'below_floor') return 'Not yet an Ember';
  return 'Ember';
}

function dateLine(item: MeEmberItem): string {
  return item.activity_at ? timeAgo(item.activity_at) : '';
}

function openLabel(item: MeEmberItem): string {
  if (!item.href) return item.state === 'stalled' ? 'With our team' : 'Waiting on Ember';
  if (item.state === 'needs_more_context' || item.state === 'below_floor') return 'Keep going';
  if (item.state === 'failed') return 'Add to it and retry';
  return 'Read';
}

function note(state: MeEmberState): ReactNode {
  switch (state) {
    case 'writing':
      return 'Your words are safe. This usually takes a moment.';
    case 'slow':
      return 'Still writing — it will be here when it is ready.';
    case 'stalled':
      return 'Quiet for too long. If it finished, it is here — if not, tell it again anytime.';
    case 'needs_more_context':
      return 'Ember could not find the lived experience in this one yet. Tap to keep going.';
    // Names what happened and what is still here: nothing was written, and the
    // words were kept. What to do about it is the two controls below, so this
    // line does not repeat them.
    case 'below_floor':
      return 'Too short to write from, so no Ember was made. Your words are still here.';
    case 'failed':
      return 'This broke on our end, not yours. Tap to add to it and we will try again.';
    default:
      return null;
  }
}
